import {
  createContext,
  useContext,
  useReducer,
  useRef,
  useEffect,
} from 'react';
import type { ReactNode, Dispatch } from 'react';

export interface InsightNote {
  id: string | number;
  text?: string;
  xPct?: number;
  yPct?: number;
  [key: string]: unknown;
}

export interface InsightApiNote {
  message: string;
  positionXRelative: number;
  positionYRelative: number;
}

export interface InsightState {
  imageUrl: string | null;
  imageBlob: Blob | null;
  notes: InsightNote[];
  step: number;
  metadata: Record<string, unknown>;
  assignees: unknown[];
  autoAssignToUsers: boolean;
  allUsers: unknown[];
  relatedGoalIds: Array<string | number>;
}

export type InsightAction =
  | { type: 'reset' }
  | {
      type: 'initWithImage';
      payload?: { blob?: Blob | null; url?: string | null };
    }
  | { type: 'setNotes'; payload?: InsightNote[] }
  | { type: 'addNote'; payload: InsightNote }
  | { type: 'updateNote'; payload: { id: string | number; text: string } }
  | { type: 'deleteNote'; payload: string | number }
  | { type: 'setStep'; payload: number }
  | { type: 'setMetadata'; payload?: Record<string, unknown> }
  | { type: 'setAssignees'; payload?: unknown[] }
  | { type: 'setAutoAssignToUsers'; payload: boolean | unknown }
  | { type: 'setAllUsers'; payload?: unknown[] }
  | { type: 'setRelatedGoalIds'; payload?: Array<string | number> };

const initialState: InsightState = {
  imageUrl: null,
  imageBlob: null,
  notes: [],
  step: 1,
  metadata: {},
  assignees: [],
  autoAssignToUsers: false,
  allUsers: [],
  relatedGoalIds: [],
};

function revokeBlobUrlIfNeeded(url: string | null): void {
  if (typeof url === 'string' && url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
}

function insightReducer(
  state: InsightState,
  action: InsightAction
): InsightState {
  switch (action.type) {
    case 'reset':
      revokeBlobUrlIfNeeded(state.imageUrl);

      return { ...initialState };

    case 'initWithImage': {
      const { blob, url } = action.payload || {};
      const nextUrl = url || null;

      if (state.imageUrl && state.imageUrl !== nextUrl) {
        revokeBlobUrlIfNeeded(state.imageUrl);
      }

      return {
        ...state,
        imageBlob: blob || null,
        imageUrl: nextUrl,
        notes: [],
        step: 1,
      };
    }

    case 'setNotes':
      return {
        ...state,
        notes: Array.isArray(action.payload) ? action.payload : [],
      };

    case 'addNote':
      return { ...state, notes: [...state.notes, action.payload] };

    case 'updateNote': {
      const { id, text } = action.payload;

      return {
        ...state,
        notes: state.notes.map((n) => (n.id === id ? { ...n, text } : n)),
      };
    }

    case 'deleteNote':
      return {
        ...state,
        notes: state.notes.filter((n) => n.id !== action.payload),
      };

    case 'setStep':
      return { ...state, step: action.payload };

    case 'setMetadata':
      return {
        ...state,
        metadata: { ...state.metadata, ...(action.payload || {}) },
      };

    case 'setAssignees':
      return {
        ...state,
        assignees: Array.isArray(action.payload) ? action.payload : [],
      };

    case 'setAutoAssignToUsers':
      return { ...state, autoAssignToUsers: Boolean(action.payload) };

    case 'setAllUsers':
      return {
        ...state,
        allUsers: Array.isArray(action.payload) ? action.payload : [],
      };

    case 'setRelatedGoalIds':
      return {
        ...state,
        relatedGoalIds: Array.isArray(action.payload) ? action.payload : [],
      };

    default:
      return state;
  }
}

interface InsightContextValue {
  state: InsightState;
  dispatch: Dispatch<InsightAction>;
}

const InsightContext = createContext<InsightContextValue | null>(null);

export const useInsight = (): InsightContextValue => {
  const ctx = useContext(InsightContext);

  if (!ctx) {
    throw new Error('useInsight must be used within an InsightProvider');
  }

  return ctx;
};

let globalDispatchRef: Dispatch<InsightAction> | null = null;

export const getInsightDispatch = (): Dispatch<InsightAction> | null =>
  globalDispatchRef;

interface PendingImage {
  blob?: Blob | null;
  url?: string | null;
}

let pendingInitImage: PendingImage | null = null;

/**
 * Queue an initWithImage action to be picked up by the InsightProvider
 * on its next mount. Call BEFORE openInsightModal().
 */
export const setPendingInsightImage = (payload: PendingImage): void => {
  pendingInitImage = payload;
};

interface InsightProviderProps {
  children: ReactNode;
}

const createInitialState = (): InsightState => {
  if (pendingInitImage) {
    const { blob, url } = pendingInitImage;

    pendingInitImage = null;

    return {
      ...initialState,
      imageBlob: blob || null,
      imageUrl: url || null,
    };
  }

  return initialState;
};

export const InsightProvider = ({ children }: InsightProviderProps) => {
  const [state, dispatch] = useReducer(
    insightReducer,
    undefined,
    createInitialState
  );
  const dispatchRef = useRef(dispatch);

  dispatchRef.current = dispatch;

  globalDispatchRef = dispatch;

  useEffect(
    () => () => {
      globalDispatchRef = null;
    },
    []
  );

  return (
    <InsightContext.Provider value={{ state, dispatch }}>
      {children}
    </InsightContext.Provider>
  );
};

export const mapNotesToApi = (
  notes: InsightNote[] | unknown
): InsightApiNote[] =>
  (Array.isArray(notes) ? (notes as InsightNote[]) : []).map((n) => ({
    message: n.text || '',
    positionXRelative: Number((n.xPct ?? 0).toFixed(4)),
    positionYRelative: Number((n.yPct ?? 0).toFixed(4)),
  }));
